import { useUserSettingsStore } from '@client/stores/userSettingsStore';
import type { RouterInputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { getTableSettingKey } from '@shared/types/user-settings';

export const LIST_TABLE_PERSIST_KEYS = {
    claims: 'claims-table-columns',
    complaints: 'complaints-table-columns-with-configurable-fields',
    incidents: 'incident-search-columns',
    obligations: 'obligations-table-columns',
    training: 'training-records-table-columns',
} as const;

/** A fresh prefetch input is exact only after persisted table settings hydrate. */
export function canPrefetchFreshListInput(): boolean {
    return useUserSettingsStore.getState().isInitialized;
}

function readPersistedPageSize(
    tableKey: (typeof LIST_TABLE_PERSIST_KEYS)[keyof typeof LIST_TABLE_PERSIST_KEYS],
): number {
    const settings = useUserSettingsStore.getState();
    if (!canPrefetchFreshListInput()) return DEFAULT_PAGE_SIZE;

    const savedPageSize: unknown = settings.getSetting(getTableSettingKey.pageSize(tableKey));
    return typeof savedPageSize === 'number' && Number.isFinite(savedPageSize) && savedPageSize > 0
        ? Math.max(1, Math.floor(savedPageSize))
        : DEFAULT_PAGE_SIZE;
}

const CLAIMS_BASE_INPUT = {
    limit: DEFAULT_PAGE_SIZE,
    where: {},
    orderBy: undefined,
    offset: 0,
} satisfies RouterInputs['claim']['listClaims'];

const INCIDENTS_BASE_INPUT = {
    limit: DEFAULT_PAGE_SIZE,
    orderBy: { id: 'desc' },
    where: {},
    offset: 0,
} satisfies RouterInputs['incident']['listIncidents'];

const COMPLAINTS_BASE_INPUT = {
    limit: DEFAULT_PAGE_SIZE,
    orderBy: { id: 'desc' },
    where: {},
    offset: 0,
} satisfies RouterInputs['complaint']['listComplaints'];

/**
 * The obligations register's view-params defaults (its "Latest version" tab) and default order. The
 * register's route passes them to `useTableViews`, which writes them into the mounted query's input,
 * and the route's `beforeLoad` prefetches `OBLIGATIONS_BASE_INPUT` built from them, so both keys agree.
 * Here rather than in the route module, which may only export `Route`.
 */
export const OBLIGATION_VIEW_PARAM_DEFAULTS = { latestPerFamily: true } as const satisfies Pick<
    RouterInputs['obligation']['listObligations'],
    'latestPerFamily'
>;

export const OBLIGATIONS_DEFAULT_ORDER_BY = { id: 'desc' } as const satisfies NonNullable<
    RouterInputs['obligation']['listObligations']['orderBy']
>;

const OBLIGATIONS_BASE_INPUT = {
    ...OBLIGATION_VIEW_PARAM_DEFAULTS,
    limit: DEFAULT_PAGE_SIZE,
    orderBy: OBLIGATIONS_DEFAULT_ORDER_BY,
    where: {},
    offset: 0,
} satisfies RouterInputs['obligation']['listObligations'];

const TRAINING_BASE_INPUT = {
    limit: DEFAULT_PAGE_SIZE,
    where: { AND: [{ disabled: false }] },
    orderBy: { userId: 'desc' },
    computedFilters: undefined,
    offset: 0,
} satisfies RouterInputs['trainingRecord']['listTrainingRecordsByUser'];

export function freshClaimsListInput(): RouterInputs['claim']['listClaims'] {
    return freshClaimsListInputWithWhere({});
}

export function freshClaimsListInputWithWhere(
    where: RouterInputs['claim']['listClaims']['where'],
): RouterInputs['claim']['listClaims'] {
    return { ...CLAIMS_BASE_INPUT, limit: readPersistedPageSize(LIST_TABLE_PERSIST_KEYS.claims), where };
}

export function freshIncidentsListInput(): RouterInputs['incident']['listIncidents'] {
    return freshIncidentsListInputWithWhere({});
}

export function freshIncidentsListInputWithWhere(
    where: RouterInputs['incident']['listIncidents']['where'],
): RouterInputs['incident']['listIncidents'] {
    return {
        ...INCIDENTS_BASE_INPUT,
        limit: readPersistedPageSize(LIST_TABLE_PERSIST_KEYS.incidents),
        where,
    };
}

export function freshComplaintsListInput(): RouterInputs['complaint']['listComplaints'] {
    return freshComplaintsListInputWithWhere({});
}

export function freshComplaintsListInputWithWhere(
    where: RouterInputs['complaint']['listComplaints']['where'],
): RouterInputs['complaint']['listComplaints'] {
    return {
        ...COMPLAINTS_BASE_INPUT,
        limit: readPersistedPageSize(LIST_TABLE_PERSIST_KEYS.complaints),
        where,
    };
}

export function freshObligationsListInput(): RouterInputs['obligation']['listObligations'] {
    return freshObligationsListInputWithWhere({});
}

export function freshObligationsListInputWithWhere(
    where: RouterInputs['obligation']['listObligations']['where'],
): RouterInputs['obligation']['listObligations'] {
    return {
        ...OBLIGATIONS_BASE_INPUT,
        limit: readPersistedPageSize(LIST_TABLE_PERSIST_KEYS.obligations),
        where,
    };
}

export function freshTrainingListInput(): RouterInputs['trainingRecord']['listTrainingRecordsByUser'] {
    return {
        ...TRAINING_BASE_INPUT,
        limit: readPersistedPageSize(LIST_TABLE_PERSIST_KEYS.training),
    };
}

export function applyTrainingFiltersToInput(
    input: RouterInputs['trainingRecord']['listTrainingRecordsByUser'],
    where: NonNullable<RouterInputs['trainingRecord']['listTrainingRecordsByUser']['where']>,
    computedFilters: RouterInputs['trainingRecord']['listTrainingRecordsByUser']['computedFilters'],
): RouterInputs['trainingRecord']['listTrainingRecordsByUser'] {
    return { ...input, where, computedFilters, offset: 0 };
}
